<template>
  <div>
    <toggle-buttons
      class="map-toggle"
      :btnList="[$t('map.default'), $t('map.wms')]"
      @btnValue="mapLayer"
      :editTB="mapBtn" />

    <div class="md-layout md-gutter" v-if="$route.params.id === undefined">
      <div class="md-layout-item md-size-25">
        <md-field :class="!checkRadius(radius) ? 'md-invalid' : ''">
          <label for="cover">{{ $t("map.radius") }} KM</label>
          <md-input type="radius" name="radius" v-model="radius" />
          <span class="md-error">{{ $t("error.errorRange") }} (5-10 KM)</span>
        </md-field>
      </div>
      <div class="md-layout-item md-size-25">
        <md-button class="md-primary md-raised" @click="cacheTiles">{{ $t("home.tiles") }}</md-button>
      </div>

      <div class="md-layout-item md-size-100">
        <p>{{ $t("map.choosePoint") }}</p>
        <div v-for="(prog, key) in progress" :key="key">
          <div v-if="prog.value > 0">
            <span>{{ prog.label }}</span>
            <b-progress
              v-if="prog.value < 100"
              :max="100"
              height="20px"
              class="tiles-progress-bar"
              show-progress
              animated>
              <b-progress-bar :label="`${((prog.value / 100) * 100).toFixed(2)}%`" :value="prog.value"></b-progress-bar>
            </b-progress>
            <p v-else class="completed"><b>{{ $t('map.completed') }}</b></p>
          </div>
        </div>
      </div>
    </div>

    <div id="map" ref="map"></div>

    <error-dialog :show="error" :text="errorText" @closeError="gpsError" />
  </div>
</template>

<script>
import ToggleButtons from '@/components/ToggleButtons'
import ErrorDialog from '@/components/ErrorDialog'

import * as L from 'leaflet'
import '@/assets/scripts/L.TileLayer.PouchDBCached'
import getCoords from '@/assets/scripts/getCoords'
import { checkRadius } from '@/assets/scripts/formatData'
import { connectionStatus } from '@/assets/scripts/checkConnection'

const defaultAttribution = `&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors - <a href="http://www.arcadiasit.it">Arcadia</a>`
const defaultUrl = 'https://{s}.tile.osm.org/{z}/{x}/{y}.png'

const accessToken = 'pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw'
const mapboxUrl = 'https://api.tiles.mapbox.com/v4/{id}/{z}/{x}/{y}.png?access_token={accessToken}'
const mapboxAttribution = `Map data &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors, <a href="http://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="http://mapbox.com">Mapbox</a>`

const wmsUrl = 'https://fmp.albania.arcadiasit.com/qgis/prog_orto_alb?SERVICE=WMS&VERSION=1.3.0&service=WMS'

const initialZoom = 10
const minZoom = 8
const maxZoom = 17
const cacheMaxAge = 12 * 3600 * 1000 // 12 hours

const minZoomCache = 10
const maxZoomCache = 15

const albanyBounds = L.latLngBounds(L.latLng(39.583, 19.000), L.latLng(42.659, 21.050))

export default {
  props: ['measurements'],
  components: {
    ToggleButtons,
    ErrorDialog
  },
  data: () => ({
    map: null,
    defaultLayer: null,
    wmsGroupLayer: null,
    mapBtn: 0,
    radius: 5,
    marker: null,
    circle: null,
    areaBounds: null,
    rectangle: null,
    error: false,
    errorText: '',
    checkRadius: checkRadius,
    defaultProgressValue: 0,
    mapboxProgressValue: 0,
    wmsProgressValue: 0
  }),
  mounted () {
    this.initMap()

    if (this.$route.params.id !== undefined) {
      this.$emit('title', this.$i18n.t('home.map'))

      this.measurements.getItem(this.$route.params.id).then(value => {
        let plotLat = value.plot.coords.latitude
        let plotLng = value.plot.coords.longitude

        if (plotLat.length > 0 && plotLng.length > 0) {
          this.map.setView(L.latLng(plotLat, plotLng))

          let plotMarker = L.marker(L.latLng(plotLat, plotLng)).addTo(this.map)
          plotMarker.bindPopup(
            `<b>${this.$i18n.t('map.plot')}</b><br>${plotLat}, ${plotLng}`,
            { closeOnClick: false, autoClose: false }
          ).openPopup()

          getCoords().then(coords => {
            let currentLat = coords.latitude
            let currentLng = coords.longitude
            let currentMarker = L.marker(L.latLng(currentLat, currentLng)).addTo(this.map)
            currentMarker.bindPopup(
              `<b>${this.$i18n.t('map.myPosition')}</b><br>${currentLat}, ${currentLng}`,
              { closeOnClick: false, autoClose: false }
            ).openPopup()

            this.wmsGroupLayer.bringToBack() // force wms layer to back
          }).catch(err => {
            this.errorText = err
            this.error = true
          })
        } else {
          this.errorText = this.$i18n.t('error.errorDist')
          this.error = true
        }
      }).catch(function (err) {
        console.log(err)
      })
    } else {
      this.$emit('title', this.$i18n.t('home.tiles'))

      this.map.setView([41.1979649, 20.0121873])
      this.map.on('click', this.onMapClick)

      let mapTap // touch event for mobile
      this.map.on('touchstart', () => {
        mapTap = setTimeout(() => {
          this.onMapClick()
        }, 500)
      })
      this.map.on('touchend,touchmove', () => {
        if (mapTap) clearTimeout(mapTap)
      })
    }
  },
  computed: {
    progress: function () {
      return [
        { label: 'Default', value: this.defaultProgressValue },
        { label: 'WMS', value: (this.mapboxProgressValue + this.wmsProgressValue) / 2 }
      ]
    }
  },
  watch: {
    radius: function () {
      if (this.circle !== null) {
        if (checkRadius(this.radius)) {
          this.circle.setRadius(this.radius * 1000)
          this.areaBounds = this.circle.getBounds()
          this.rectangle.remove()
          this.rectangle = L.rectangle(this.areaBounds).addTo(this.map)
        } else {
          this.rectangle.remove()
        }
      }
    }
  },
  methods: {
    mapLayer: function (value) {
      if (value === 1) {
        this.mapBtn = 1
        this.wmsGroupLayer.bringToFront()
      } else {
        this.mapBtn = 0
        this.wmsGroupLayer.bringToBack()
      }
    },

    initMap: function () {
      this.setDefault()
      this.setWmsGroup()

      this.map = L.map(this.$refs.map.id, {
        zoom: initialZoom,
        minZoom: minZoom,
        maxZoom: maxZoom,
        maxBounds: albanyBounds,
        layers: [this.defaultLayer, this.wmsGroupLayer]
      })
      /* let baseMaps = {
        'WMS': this.wmsGroupLayer,
        'Default': this.defaultLayer
      }
      L.control.layers(baseMaps).addTo(this.map) */
    },

    setDefault: function () {
      this.defaultLayer = L.tileLayer(defaultUrl, {
        attribution: defaultAttribution,
        useCache: true,
        cacheMaxAge: cacheMaxAge,
        crossOrigin: true
      })
    },

    setWmsGroup: function () {
      let mapboxLayer = L.tileLayer(mapboxUrl, {
        id: 'mapbox.streets',
        accessToken: accessToken,
        attribution: mapboxAttribution,
        useCache: true,
        cacheMaxAge: cacheMaxAge,
        crossOrigin: true
      })

      let wmsLayer = L.tileLayer.wms(wmsUrl, {
        layers: 'prog_orto_alb',
        opacity: 0.9,
        bounds: albanyBounds,
        useCache: true,
        cacheMaxAge: cacheMaxAge,
        crossOrigin: true
      })

      this.wmsGroupLayer = L.featureGroup([mapboxLayer, wmsLayer])
    },

    gpsError: function () {
      this.error = false
      this.$router.push('/')
    },

    onMapClick: function (e) {
      // console.log('You clicked the map at ' + e.latlng)
      if (this.marker !== null) {
        this.marker.remove()
        this.circle.remove()
        this.rectangle.remove()
      }
      this.marker = L.marker(e.latlng).addTo(this.map)
      this.circle = L.circle(e.latlng, {
        color: 'transparent',
        radius: this.radius * 1000
      }).addTo(this.map)
      this.areaBounds = this.circle.getBounds()
      this.rectangle = L.rectangle(this.areaBounds).addTo(this.map)
      // map.fitBounds(areaBounds)
    },

    cacheTiles: function () {
      if (checkRadius(this.radius) && this.marker !== null) {
        if (connectionStatus()) {
          this.addCheckProgress(this.defaultLayer, 'defaultProgressValue')
          this.defaultLayer.seed(this.areaBounds, minZoomCache, maxZoomCache)

          this.wmsGroupLayer.eachLayer(layer => {
            layer.wmsParams === undefined ? this.addCheckProgress(layer, 'mapboxProgressValue') : this.addCheckProgress(layer, 'wmsProgressValue')
            layer.seed(this.areaBounds, minZoomCache, maxZoomCache)
          })
        }
      }
    },

    addCheckProgress: function (layer, layerProgressValue) {
      layer.on('seedprogress', (seedData) => {
        // console.log(layerProgressValue)
        this[layerProgressValue] = 100 - Math.floor(seedData.remainingLength / seedData.queueLength * 100)
      })
      /* layer.on('seedend', () => {
        console.log(layerProgressValue + ' COMPLETE')
      }) */
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/assets/style/arcadia_theme.scss';

  #map {
    width: 100%;
    height: 70vh;
    z-index: 0 !important;
  }

  .map-toggle {
    margin-top: 0 !important;
    margin-bottom: 16px !important;
  }

  .completed {
    color: var(--md-primary);
    margin-bottom: 10px;
  }

  .tiles-progress-bar {
    margin-bottom: 10px;
  }
</style>
